<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col cols="auto" class="d-flex align-center">
          <v-breadcrumbs :items="breadcrumbs" class="pa-0" />
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <v-tooltip max-width="300" bottom>
            <template #activator="{ on }">
              <base-chip v-show="hasChanges" color="blue" text="Changes Detected" icon="info" dark small v-on="on">
              </base-chip>
            </template>
            <span>Click the FAB in the bottom right then click "Save Site" to keep your changes!</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-divider />
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col sm="3">
          <v-card outlined>
            <v-tabs v-model="query.params.tab" vertical>
              <template v-for="tabCategory in tabCategories">
                <v-tab v-if="tabCategory.show" :key="tabCategory.category">{{
                  tabCategory.tab || tabCategory.category
                }}</v-tab>
              </template>
              <v-tab>Languages</v-tab>
              <v-tab v-if="updatedSite.department === 'organic'">Pages</v-tab>
            </v-tabs>
          </v-card>
        </v-col>
        <v-col sm="9">
          <v-tabs-items v-model="query.params.tab" class="transparent">
            <template v-for="tabCategory in tabCategories">
              <template v-if="tabCategory.show">
                <v-tab-item :key="tabCategory.category" transition="false">
                  <category-options
                    :data="updatedSite"
                    :fields="categorisedSchema.general[tabCategory.category]"
                    :is-disabled="isLoading || !isPermittedTo('update')"
                    :options="{ siteGroups, adgroups, landingPageTemplates }"
                    :category="tabCategory.category"
                    :component-cols="tabCategory.componentCols || 12"
                    :show-category="false"
                    @input="onInput"
                  />
                </v-tab-item>
              </template>
            </template>
            <v-tab-item transition="false">
              <v-container>
                <v-row>
                  <v-col class="px-0">
                    <v-card outlined>
                      <v-data-table
                        hide-default-footer
                        :headers="languageHeaders"
                        :items="siteLanguages"
                        no-data-text="No languages set"
                      >
                        <template #item="{ item }">
                          <v-hover v-slot="{ hover }">
                            <tr>
                              <td>
                                <div class="d-flex align-center">
                                  <flag-pip v-if="item.flag" :flag="item.flag" class="mr-3" />
                                  <span>{{ item.name }}</span>
                                </div>
                              </td>
                              <td>{{ item.code.toUpperCase() }}</td>
                              <td class="text-right">
                                <div v-show="hover">
                                  <more-menu>
                                    <v-list-item :to="{ name: 'languages.view', params: { id: item.id } }">
                                      <v-list-item-title>
                                        <v-icon left small>launch</v-icon>
                                        <span>View Language</span>
                                      </v-list-item-title>
                                    </v-list-item>
                                    <v-list-item
                                      :disabled="!isPermittedTo('update')"
                                      @click="onDeleteLanguageClick(item)"
                                    >
                                      <v-list-item-title>
                                        <v-icon left small>delete</v-icon>
                                        <span>Remove Language</span>
                                      </v-list-item-title>
                                    </v-list-item>
                                  </more-menu>
                                </div>
                              </td>
                            </tr>
                          </v-hover>
                        </template>
                      </v-data-table>
                      <v-card-actions class="mt-6">
                        <select-list
                          v-model="newLanguageId"
                          label="Language"
                          :items="languageOptions"
                          class="mr-6"
                          outlined
                          hide-details
                        />
                        <v-btn color="primary" :disabled="!newLanguageId" depressed @click="showAddLanguageModal = true"
                          >Add Language</v-btn
                        >
                      </v-card-actions>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
            <template v-if="updatedSite.department === 'organic'">
              <v-tab-item transition="false">
                <v-container>
                  <v-row>
                    <v-col class="px-0">
                      <v-card outlined>
                        <v-data-table
                          :loading="isLoading || isPagesLoading"
                          :headers="pageHeaders"
                          :items="pages"
                          :server-items-length="pagesPagination.total"
                          :page="pagesPagination.page"
                          :items-per-page="pagesPagination.perPage"
                          :footer-props="{ itemsPerPageOptions: [20, 50, 100, -1] }"
                          no-data-text="No pages exist for this site"
                          disable-sort
                          @update:page="onPagesTablePageUpdate"
                          @update:items-per-page="onPagesTablePerPageUpdate"
                        >
                          <template #item="{ item }">
                            <v-hover v-slot="{ hover }">
                              <tr>
                                <td :title="item.meta.headline">
                                  {{ item.meta.headline ? truncate(item.meta.headline, 32) : null }}
                                </td>
                                <td>
                                  <div class="d-flex align-center">
                                    <flag-pip :flag="item.language.flag" class="mr-2" />
                                    <span>{{ item.language.name }}</span>
                                  </div>
                                </td>
                                <td>{{ item.pageType.name }}</td>
                                <td>
                                  <published-chip :value="item.wp_publish_status" small />
                                </td>
                                <td>
                                  <div v-show="hover">
                                    <more-menu>
                                      <v-list-item :to="{ name: 'content.pages.edit', params: { id: item.id } }">
                                        <v-list-item-title>
                                          <v-icon left small>launch</v-icon>
                                          <span>View Page</span>
                                        </v-list-item-title>
                                      </v-list-item>
                                      <v-list-item :to="{ name: 'page-types.view', params: { id: item.pageType.id } }">
                                        <v-list-item-title>
                                          <v-icon left small>launch</v-icon>
                                          <span>View Page Type</span>
                                        </v-list-item-title>
                                      </v-list-item>
                                    </more-menu>
                                  </div>
                                </td>
                              </tr>
                            </v-hover>
                          </template>
                        </v-data-table>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-container>
              </v-tab-item>
            </template>
          </v-tabs-items>
        </v-col>
      </v-row>
    </v-container>

    <fab-actions
      :can-delete="isPermittedTo('delete')"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Site"
      @delete="showDeleteModal = true"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
    />

    <!-- Reset Modal -->
    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="site"
      @modal-confirm="onResetConfirm"
    />

    <!-- Save Modal -->
    <generic-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Site"
      persistent
      @modal-confirm="onSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <p>Are you sure you want to save these changes?</p>
    </generic-modal>

    <!-- Delete Modal -->
    <generic-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Delete Site"
      confirm-color="error"
      confirm-text="Delete"
      @modal-confirm="onDeleteConfirm"
    >
      <div>
        Are you sure you want to <span class="red--text font-weight-bold">DELETE</span> this site? This cannot be
        undone.
      </div>
    </generic-modal>

    <!-- Add Language Modal -->
    <generic-modal
      v-model="showAddLanguageModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Add Site Language"
      @modal-confirm="onAddLanguageConfirm"
    >
      Are you sure you want to add <strong>{{ getOptionKeyByValue(languageOptions, newLanguageId) }}</strong> as a site
      language?
    </generic-modal>

    <!-- Delete Language Modal -->
    <generic-modal
      v-model="showDeleteLanguageModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Remove Site Language"
      confirm-color="error"
      confirm-text="Remove"
      @modal-confirm="onDeleteLanguageConfirm"
    >
      Are you sure you want to remove
      <strong>{{ getOptionKeyByValue(languageOptions, languageIdToRemove) }}</strong> from site languages?
    </generic-modal>

    <!-- Exit Page Modal -->
    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Helpers
import { mapMutations, mapState } from 'vuex'
import { cloneDeep, capitalize } from 'lodash-es'
import getOptionKeyByValue from '@/utils/get-option-key-by-value'
// Services
import { languages, landingPages, languageSites, pages, sites, siteGroups } from '@/services/offers'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Components
import BaseChip from '@/components/chips/base-chip'
import CategoryOptions from '@/components/category-options'
import ExitModal from '@/components/modals/exit-modal'
import FabActions from '@/components/fab-actions'
import FlagPip from '@/views/news-articles/components/flag-pip'
import GenericModal from '@/components/modals/generic-modal'
import MoreMenu from '@/components/more-menu'
import PageLoader from '@/components/page-loader'
import PublishedChip from '@/views/news-articles/components/published-chip'
import ResetModal from '@/components/modals/reset-modal'
import SelectList from '@/components/forms/select-list'

export default {
  name: 'SiteEdit',
  components: {
    BaseChip,
    CategoryOptions,
    ExitModal,
    FabActions,
    FlagPip,
    GenericModal,
    MoreMenu,
    PageLoader,
    PublishedChip,
    ResetModal,
    SelectList,
  },
  mixins: [DataModification, PermissionsHandler, QueryParamsHandler],
  data: () => ({
    adgroups: [],
    defaultQuery: {
      tab: 0,
    },
    isLoading: false,
    isPagesLoading: false,
    landingPageTemplates: [],
    languageHeaders: [
      { text: 'Language', value: 'name' },
      { text: 'Code', value: 'code' },
      { text: '', value: 'actions', align: 'right', sortable: false, width: '70px' },
    ],
    languageIdToRemove: null,
    languages: [],
    newLanguageId: null,
    originalSite: {},
    pageHeaders: [
      { text: 'Headline', value: 'headline' },
      { text: 'Language', value: 'language' },
      { text: 'Page Type', value: 'pageType.name' },
      { text: 'WP Status', value: 'wp_publish_status' },
      { text: '', value: 'actions', width: '70px' },
    ],
    pages: [],
    pagesPagination: {
      perPage: 20,
      page: 1,
      total: 0,
    },
    queriesDefined: ['tab'],
    queryTypes: { tab: 'number' },
    showAddLanguageModal: false,
    showDeleteLanguageModal: false,
    showDeleteModal: false,
    showResetModal: false,
    showSaveModal: false,
    siteLanguages: [],
    siteGroups: [],
    updatedSite: {},
  }),
  computed: {
    ...mapState('sites', ['categorisedSchema']),
    breadcrumbs() {
      return [
        { text: 'Site Management', to: { name: 'sites.management' }, exact: true },
        { text: this.originalSite?.name || 'loading', disabled: true },
      ]
    },
    languageOptions() {
      return this.languages.map(({ id, name }) => ({
        key: name,
        value: id,
        disabled: this.siteLanguages.find(({ id: language_id }) => language_id === id),
      }))
    },
    tabCategories() {
      return [
        { category: 'General', show: true },
        { category: 'Contact', show: true },
        {
          category: 'Funnels',
          show: this.siteGroups.find(({ value }) => value === this.updatedSite.site_group_id)?.slug === 'funnels',
        },
        {
          category: capitalize(this.updatedSite.department),
          show: this.updatedSite.department && this.categorisedSchema.general[capitalize(this.updatedSite.department)],
        },
        { category: 'Legal', show: true },
        { category: 'Theme', show: true, componentCols: 6 },
        { tab: 'Other', category: 'General Information', show: this.categorisedSchema.general['General Information'] },
      ]
    },
    hasChanges() {
      return this.hasDataChanged(this.originalSite, this.updatedSite)
    },
  },
  async created() {
    await Promise.all([this.getSite(), this.getSiteGroups(), this.getLanguages(), this.getSiteLanguages()])
    await Promise.all([this.getAdgroups(), this.getLandingPages()])
    this.getPages()
    this.setPageLoading(false)
    this.setBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapMutations({ setPageLoading: 'general/setPageLoading' }),
    capitalize,
    getOptionKeyByValue,
    async getLanguages() {
      try {
        this.languages = await languages.getLanguages()
      } catch (e) {
        this.$notify({ type: 'error', text: 'Unable to fetch languages' })
      }
    },
    async getSiteLanguages() {
      try {
        const response = await languageSites.getAll({ filters: { site_id: this.$route.params.id } })
        this.siteLanguages = response.map(({ language }) => language)
      } catch (e) {
        this.$notify({ type: 'error', text: 'Unable to fetch site languages' })
      }
    },
    async getAdgroups() {
      this.isLoading = true
      try {
        const params = { filters: { site: this.updatedSite.hostname } }
        const { data: adgroups } = await landingPages.getLandingPages(params)
        this.adgroups = adgroups.map(({ adgroupName }) => ({ key: adgroupName, value: adgroupName }))
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to fetch default adgroup options', data: { error }, duration: -1 })
      }
      this.isLoading = false
    },
    async getLandingPages() {
      this.isLoading = true
      try {
        const data = await landingPages.getAllLandingPageTemplates()
        this.landingPageTemplates = data.map(({ template, slug }) => ({
          key: template.default.templateName,
          value: slug,
        }))
      } catch (error) {
        this.$notify({ type: 'error', text: 'Unable to fetch funnel override options', data: { error }, duration: -1 })
      }
      this.isLoading = false
    },
    async getPages() {
      this.isPagesLoading = true
      try {
        const { perPage, page } = this.pagesPagination

        const {
          data,
          pagination: { total },
        } = await pages.getAll({
          filters: { site_id: this.$route.params.id },
          paginate: { perPage: perPage === -1 ? 99999 : perPage, page },
        })

        this.pages = data
        this.pagesPagination.total = total
      } catch (e) {
        this.$notify({ type: 'error', text: 'Unable to fetch site pages' })
      }
      this.isPagesLoading = false
    },
    async getSite() {
      this.isLoading = true
      try {
        this.originalSite = await sites.get(this.$route.params.id)
        this.updatedSite = cloneDeep(this.originalSite)
      } catch (e) {
        this.$notify({ type: 'error', text: 'Unable to find site' })
        this.$router.push({ name: 'sites.management' })
      }
      this.isLoading = false
    },
    async getSiteGroups() {
      this.isLoading = true
      try {
        const { data } = await siteGroups.getAll({ paginate: { perPage: null } })
        this.siteGroups = data.map(({ name, id, slug }) => ({ key: name, value: id, slug }))
      } catch (e) {
        this.$notify({ type: 'error', text: 'Unable to fetch site groups' })
      }
      this.isLoading = false
    },
    async onAddLanguageConfirm() {
      this.isLoading = true
      try {
        await languageSites.attach(this.originalSite.id, this.newLanguageId)
        const languageName = this.getOptionKeyByValue(this.languageOptions, this.newLanguageId)
        this.$notify({
          type: 'success',
          text: `Successfully added ${languageName} as a site language`,
        })

        this.showAddLanguageModal = false
        await this.getSiteLanguages()
        this.newLanguageId = null
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to assign language to site',
        })
      }
      this.isLoading = false
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    async onDeleteLanguageClick({ id }) {
      this.languageIdToRemove = id
      this.showDeleteLanguageModal = true
    },
    async onDeleteLanguageConfirm() {
      this.isLoading = true
      try {
        await languageSites.detach(this.originalSite.id, this.languageIdToRemove)
        const languageName = this.getOptionKeyByValue(this.languageOptions, this.languageIdToRemove)
        this.$notify({
          type: 'success',
          text: `Successfully removed ${languageName} from site languages`,
        })

        this.languageIdToRemove = null
        this.showDeleteLanguageModal = false
        await this.getSiteLanguages()
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to remove language from site',
        })
      }
      this.isLoading = false
    },
    async onDeleteConfirm() {
      this.isLoading = true
      try {
        await sites.delete(this.$route.params.id)
        this.$notify({ type: 'success', text: `Successfully deleted site: ${this.originalSite.name}` })
        this.$router.push({ name: 'sites.management' })
      } catch (e) {
        this.$notify({ type: 'error', text: 'Unable to delete site' })
      }
      this.isLoading = false
    },
    onInput({ context, model, value }) {
      if (context) {
        if (this.updatedSite[model]) {
          this.$set(this.updatedSite[model], context, value)
        } else {
          this.$set(this.updatedSite, model, { [context]: value })
        }
      } else this.$set(this.updatedSite, model, value)
    },
    onPagesTablePageUpdate(page) {
      this.pagesPagination.page = page
      this.getPages()
    },
    onPagesTablePerPageUpdate(perPage) {
      this.pagesPagination.perPage = perPage
      this.onPagesTablePageUpdate(1)
    },
    onResetConfirm() {
      this.updatedSite = cloneDeep(this.originalSite)
      this.$notify({ type: 'info', text: 'Changes have been discarded' })
      this.showResetModal = false
    },
    async onSaveConfirm() {
      await this.updateSite()
      this.showSaveModal = false
    },
    truncate(string, length) {
      if (string.length <= length) return string
      return string.slice(0, length) + '...'
    },
    async updateSite() {
      this.isLoading = true
      try {
        await sites.updateSite(this.$route.params.id, this.updatedSite)
        this.$notify({ type: 'success', text: 'Successfully updated site' })
        await this.getSite()
      } catch (e) {
        this.$notify({ type: 'error', text: 'Unable to update site' })
      }
      this.isLoading = false
    },
  },
}
</script>
